import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { filterUpsellItems } from './upsellSearch';
import { roundUpsellQuantityToPallet, isPalletOnlyUpsell } from './gazUpsellQuantityRules.js';
import { palletsWord } from '../../utils/gazDeclensions.js';
import { formatMoney, formatNomenclatureName, formatNumber, formatUnit } from '../../utils/gazTextFormat.js';
import {
  parsePriceValue,
  hasUsablePrice,
  palletsNeededFor,
  unitsPerPalletOf,
  palletVolumeOf,
  getUpsellStock,
} from './gazUpsellMetrics.js';
import {
  addButtonLabel,
  itemsWord,
  palletPlacesWord,
  priceOf,
  selectionTotalPrice,
  splitByPrice,
  maxQuantityFor,
  upsellCapacityState,
} from './gazUpsellSelection.js';
import { GAZ_PRICE_ON_REQUEST } from './gazGlueAvailability.js';
import './gazUpsellModal.css';

// Окно дозагрузки для МСК Газобетона.
//
// Товары приходят готовым списком — тем же, по которому решается, показывать ли
// кнопку. Своего поиска и своего вычисления доступности здесь нет: список
// фильтруется общим filterUpsellItems.
//
// Ограничение по вместимости считается в паллетоместах по нормам, уже пришедшим
// с позицией (gazUpsellMetrics). Цены не пересчитываются.

export { parsePriceValue, hasUsablePrice, palletsNeededFor };

// Числа и суммы — общими форматтерами МСК Газобетона: дробная часть через
// запятую, разряды обычным пробелом. Своих копий здесь больше нет.
const fmt = formatNumber;
const money = (value) => formatMoney(value);

// U-блоки завод отгружает только целыми поддонами — количество шагает поддоном.
// Клей и добавка отгружаются поштучно. Правило общее с правкой количества в
// карточке машины, см. gazUpsellQuantityRules.
const isPalletOnlyItem = (item) => isPalletOnlyUpsell(item);

const itemKeyOf = (item, index) => (
  `${String(item?.product_calculator_name || index).trim().toLowerCase()}::${String(item?.manufacturer || '').trim().toLowerCase()}`
);

/** «36 шт. в поддоне» — упаковка позиции. */
const capacityTextOf = (item) => {
  const perPallet = unitsPerPalletOf(item);
  if (perPallet) {
    return `${fmt(perPallet)} ${formatUnit(item?.unit) || 'шт'}. в поддоне`;
  }
  const palletVolume = palletVolumeOf(item);
  if (palletVolume) {
    return `${fmt(palletVolume)} м³ в поддоне`;
  }
  return null;
};

const GazUpsellModal = ({ vehicle, items, paymentMethod, onClose, onSubmit }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [quantities, setQuantities] = useState({});
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [showPriceless, setShowPriceless] = useState(false);
  // Строки, где менеджер действительно пробовал набрать количество. Красная
  // ошибка про «не помещается» показывается только им: место, занятое другой
  // выбранной позицией, — это не ошибка этой строки.
  const [attemptedKeys, setAttemptedKeys] = useState([]);
  const searchInputRef = useRef(null);

  const list = useMemo(() => (Array.isArray(items) ? items : []), [items]);

  // Список пересобирается родителем на каждый его рендер, поэтому сбрасываемся по
  // содержимому, а не по ссылке на массив: иначе выбор менеджера обнулялся бы
  // от любого постороннего перерисовывания.
  const listSignature = useMemo(
    () => list.map((item, index) => itemKeyOf(item, index)).join('|'),
    [list]
  );

  useEffect(() => {
    const initial = {};
    list.forEach((item, index) => {
      const perPallet = unitsPerPalletOf(item);
      initial[itemKeyOf(item, index)] = perPallet && isPalletOnlyItem(item) ? String(perPallet) : '1';
    });
    setQuantities(initial);
    setSelectedKeys([]);
    setAttemptedKeys([]);
  }, [list, listSignature]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const freePallets = Number.isFinite(Number(vehicle?.freePallets)) ? Number(vehicle.freePallets) : null;
  // Объём одного паллетоместа берём из уже готовых цифр машины, своей арифметики нет.
  const palletVolume = (() => {
    const freeVolume = Number(vehicle?.freeVolume);
    if (freePallets > 0 && Number.isFinite(freeVolume) && freeVolume > 0) {
      return freeVolume / freePallets;
    }
    const maxVolume = Number(vehicle?.maxVolume);
    const capacity = Number(vehicle?.capacity);
    if (Number.isFinite(maxVolume) && maxVolume > 0 && Number.isFinite(capacity) && capacity > 0) {
      return maxVolume / capacity;
    }
    return null;
  })();

  const quantityOf = (key) => quantities[key];

  const rows = useMemo(() => filterUpsellItems(list, searchQuery).map((item) => ({
    item,
    key: itemKeyOf(item, list.indexOf(item)),
  })), [list, searchQuery]);

  // Товары с ценой идут первыми, без цены — отдельной группой внизу.
  const { priced: pricedRows, priceless: pricelessRows } = useMemo(
    () => splitByPrice(rows, paymentMethod),
    [rows, paymentMethod]
  );
  // Если запрос совпал только с позициями без цены, показываем их сразу.
  const pricelessOpen = showPriceless || (pricedRows.length === 0 && pricelessRows.length > 0);

  const selectedRows = useMemo(() => list
    .map((item, index) => ({ item, key: itemKeyOf(item, index) }))
    .filter((row) => selectedKeys.includes(row.key)), [list, selectedKeys]);

  const palletsOf = (item, key) => palletsNeededFor(item, quantityOf(key));

  const selectedPallets = selectedRows.reduce((sum, row) => sum + (palletsOf(row.item, row.key) || 0), 0);
  const hasUnmeasuredSelection = selectedRows.some((row) => palletsOf(row.item, row.key) === null);
  // Одно состояние свободного места на всё окно: остаток, превышение и «место
  // разобрано целиком». Считает общая функция — своей вместимости здесь нет.
  const capacityState = upsellCapacityState({
    freePallets,
    selectedPallets,
    selectedCount: selectedRows.length,
  });
  const isOverCapacity = capacityState.isOver;
  const isCapacityFull = capacityState.isFull;

  const hasInvalidQuantity = selectedRows.some((row) => {
    const qty = Number(String(quantityOf(row.key) ?? '').replace(',', '.'));
    return !Number.isFinite(qty) || qty <= 0;
  });

  // Позицию без цены выбрать нельзя — кнопка выбора у неё отключена. Проверка здесь
  // страхует от того, что такая позиция всё же окажется в выборе: добавлять товар
  // по 0 ₽ нельзя ни при каких обстоятельствах.
  const hasPricelessSelection = selectedRows.some((row) => !hasUsablePrice(
    paymentMethod === 'cash' ? row.item?.price_cash : row.item?.price_non_cash
  ));

  const markAttempt = (key) => {
    setAttemptedKeys((prev) => (prev.includes(key) ? prev : [...prev, key]));
  };

  const setQuantity = (key, value) => {
    if (value === '' || /^[0-9]*\.?[0-9]*$/.test(value)) {
      markAttempt(key);
      setQuantities((prev) => ({ ...prev, [key]: value }));
    }
  };

  const stepByPallet = (item, key, direction) => {
    const perPallet = unitsPerPalletOf(item);
    if (!perPallet) return;
    markAttempt(key);
    // Текущее количество читаем внутри обновления состояния: два быстрых клика
    // иначе отсчитывались бы от одного и того же значения.
    setQuantities((prev) => {
      const current = Number(prev[key]) || 0;
      const currentPallets = Math.max(Math.round(current / perPallet), 1);
      const nextPallets = Math.max(currentPallets + direction, 1);
      return { ...prev, [key]: String(nextPallets * perPallet) };
    });
  };

  // Количество дотягивается до целого поддона, когда менеджер закончил набирать.
  // На каждой цифре это делать нельзя: набирая «108», человек проходит через «1».
  const roundQuantityToPallet = (item, key) => {
    const rounded = roundUpsellQuantityToPallet({ material: item, quantity: quantityOf(key) });
    if (!rounded || !rounded.changed) {
      return;
    }
    markAttempt(key);
    setQuantities((prev) => ({ ...prev, [key]: String(rounded.quantity) }));
  };

  const toggleSelected = (key) => {
    markAttempt(key);
    setSelectedKeys((prev) => (prev.includes(key)
      ? prev.filter((candidate) => candidate !== key)
      : [...prev, key]));
  };

  // Сколько мест доступно этой позиции: свободные минус занятые другим выбором.
  const allowanceFor = (key) => {
    if (freePallets === null) return null;
    const others = selectedRows
      .filter((row) => row.key !== key)
      .reduce((sum, row) => sum + (palletsOf(row.item, row.key) || 0), 0);
    return freePallets - others;
  };

  const handleSubmit = () => {
    if (selectedRows.length === 0 || isOverCapacity || hasInvalidQuantity || hasPricelessSelection) {
      return;
    }
    // Цены отдаём уже нормализованными числами. Сборка позиции заказа читает
    // price_cash/price_non_cash через parseInt, а он обрывает «1 250,00» на пробеле
    // и добавил бы товар по 1 ₽. Значения не выдумываются — это та же цена
    // источника, только разобранная правильно.
    onSubmit(selectedRows.map((row) => {
      const cash = parsePriceValue(row.item?.price_cash);
      const nonCash = parsePriceValue(row.item?.price_non_cash);
      const stock = getUpsellStock(row.item);
      return {
        ...row.item,
        ...(cash !== null ? { price_cash: cash } : {}),
        ...(nonCash !== null ? { price_non_cash: nonCash } : {}),
        // Наличие переносится в позицию тем же правилом, что показано в окне:
        // ничего нового не выдумывается, поле появляется только когда правило
        // сработало. Путь одиночного добавления вне области не затрагивается.
        ...(stock ? { gazStockNote: stock.short } : {}),
        quantity: Number(String(quantityOf(row.key) ?? '').replace(',', '.')),
      };
    }));
  };

  // Всё из готовой модели машины: своей вместимости и своего распределения нет.
  const capacity = Number.isFinite(Number(vehicle?.capacity)) ? Number(vehicle.capacity) : null;
  const loadedPallets = Number.isFinite(Number(vehicle?.loadedPallets)) ? Number(vehicle.loadedPallets) : null;
  const production = String(
    selectedRows[0]?.item?.manufacturer || list[0]?.manufacturer || ''
  ).trim();

  // Стоимость выбранного пересчитывается на каждый рендер, поэтому меняется сразу
  // после выбора, правки количества и снятия позиции.
  const selectedTotal = selectionTotalPrice({ rows: selectedRows, quantities, paymentMethod });

  // Объёмы берутся из готовой модели машины; объём выбранного — из того же
  // паллетоместа, которым считается место. Своей арифметики рейсов нет.
  const loadedVolume = Number.isFinite(Number(vehicle?.loadedVolume)) ? Number(vehicle.loadedVolume) : null;
  const maxVolume = Number.isFinite(Number(vehicle?.maxVolume)) ? Number(vehicle.maxVolume) : null;
  const selectedVolume = palletVolume ? selectedPallets * palletVolume : null;

  // «После добавления» — занято плюс выбранное, без пересчёта чего-либо ещё.
  const afterPallets = loadedPallets !== null ? loadedPallets + selectedPallets : null;
  const afterVolume = loadedVolume !== null && selectedVolume !== null
    ? loadedVolume + selectedVolume
    : null;

  const palletsOutOf = (value, total) => (
    value !== null && total !== null
      ? `${fmt(value)} из ${fmt(total)} ${palletPlacesWord(total)}`
      : null
  );
  const volumeOutOf = (value, total) => (
    value !== null && total !== null ? `${fmt(value)} из ${fmt(total)} м³` : null
  );

  return createPortal(
    <div className="gaz-upsell-overlay" role="presentation">
      <div className="gaz-upsell" role="dialog" aria-modal="true" aria-labelledby="gaz-upsell-title">
        <div className="gaz-upsell__head">
          <div className="gaz-upsell__head-text">
            <h3 id="gaz-upsell-title" className="gaz-upsell__title">
              Дозагрузка {vehicle?.type ? vehicle.type.toLowerCase() === 'шаланда' ? 'шаланды' : 'манипулятора' : 'машины'}
            </h3>
            {production && <p className="gaz-upsell__production">{production}</p>}
          </div>
          <button type="button" className="gaz-upsell__close" onClick={onClose} aria-label="Закрыть">
            ✕
          </button>
        </div>

        <div className="gaz-upsell__body">
          {/* Четыре показателя одинаковой ширины: сколько машина везёт сейчас,
              что добавляет менеджер, что получится и сколько это стоит. Свободное
              место названо в карточке машины, поэтому отдельной колонкой не
              дублируется, а «останется» читается из «после добавления». */}
          <div className="gaz-upsell__stats">
            <div className="gaz-upsell__stat">
              <span className="gaz-upsell__stat-label">Занято</span>
              <b className="gaz-upsell__stat-value">
                {palletsOutOf(loadedPallets, capacity) || '—'}
              </b>
              {volumeOutOf(loadedVolume, maxVolume) && (
                <span className="gaz-upsell__stat-sub">{volumeOutOf(loadedVolume, maxVolume)}</span>
              )}
            </div>
            <div className="gaz-upsell__stat">
              <span className="gaz-upsell__stat-label">Выбрано</span>
              <b className={`gaz-upsell__stat-value${isOverCapacity ? ' is-over' : ''}`}>
                {`${fmt(selectedPallets)} ${palletsWord(selectedPallets)}`}
              </b>
              {selectedVolume !== null && (
                <span className="gaz-upsell__stat-sub">{`${fmt(selectedVolume)} м³`}</span>
              )}
            </div>
            <div className="gaz-upsell__stat">
              <span className="gaz-upsell__stat-label">После добавления</span>
              {/* При превышении показывается настоящее значение и красится
                  красным: подменять его вместимостью значило бы показать
                  состояние, которого нет. Само превышение объяснено ниже. */}
              <b className={`gaz-upsell__stat-value${isOverCapacity ? ' is-over' : ''}`}>
                {palletsOutOf(afterPallets, capacity) || '—'}
              </b>
              {volumeOutOf(afterVolume, maxVolume) && (
                <span className={`gaz-upsell__stat-sub${isOverCapacity ? ' is-over' : ''}`}>
                  {volumeOutOf(afterVolume, maxVolume)}
                </span>
              )}
            </div>
            <div className="gaz-upsell__stat">
              <span className="gaz-upsell__stat-label">Стоимость</span>
              <b className="gaz-upsell__stat-value">{`${money(selectedTotal)} ₽`}</b>
            </div>
          </div>

          <p className="gaz-upsell__note">Дозагрузка не увеличит количество рейсов</p>

          {isOverCapacity && (
            <div className="gaz-upsell__alert">
              Выбрано больше, чем помещается: нужно {fmt(selectedPallets)} {palletsWord(selectedPallets)},
              свободно {fmt(freePallets)}. Уменьшите количество или снимите позицию — иначе понадобится
              ещё один рейс.
            </div>
          )}

          {hasUnmeasuredSelection && !isOverCapacity && (
            <div className="gaz-upsell__alert gaz-upsell__alert--soft">
              У части выбранных позиций не заведена норма поддона — занятое ими место не посчитано.
              Проверьте загрузку вручную.
            </div>
          )}

          {/* Одно спокойное сообщение вместо красной строки у каждой оставшейся
              позиции: место кончилось не по ошибке менеджера, а потому что его
              занял его же выбор. Как только место освободится, сообщение уйдёт
              само — состояние вычисляется, а не хранится. */}
          {isCapacityFull && (
            <div className="gaz-upsell__alert gaz-upsell__alert--info" role="status">
              <b className="gaz-upsell__alert-title">Свободное место распределено полностью</b>
              <span className="gaz-upsell__alert-text">
                Уберите выбранный товар или уменьшите количество, чтобы выбрать другой.
              </span>
            </div>
          )}

          <div className="gaz-upsell__search">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Поиск по наименованию и категории"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
          </div>

          <div className="gaz-upsell__list">
            {rows.length === 0 && (
              <div className="gaz-upsell__empty">Нет материалов, соответствующих запросу</div>
            )}

            {pricedRows.map(({ item, key }) => {
              const isSelected = selectedKeys.includes(key);
              const perPallet = unitsPerPalletOf(item);
              const quantity = quantityOf(key);
              const pallets = palletsNeededFor(item, quantity);
              const allowance = allowanceFor(key);
              const doesNotFit = allowance !== null && pallets !== null && pallets > allowance;
              const capacityText = capacityTextOf(item);
              const price = paymentMethod === 'cash' ? item.price_cash : item.price_non_cash;
              // Позицию без действительной цены выбрать нельзя: добавить товар по 0 ₽
              // значит отдать его бесплатно. Цену здесь не придумываем и не подставляем —
              // её должен принести источник (см. hasUsablePrice).
              const priceIsUsable = hasUsablePrice(price);
              const stock = getUpsellStock(item);
              const currentPallets = perPallet
                ? Math.max(Math.round((Number(quantity) || perPallet) / perPallet), 1)
                : null;
              const stepDisabled = allowance !== null && perPallet && (currentPallets + 1) > allowance;
              // Предел этой позиции: свободные места за вычетом других выбранных.
              const maxHint = maxQuantityFor({
                item,
                allowancePallets: allowance,
                currentQuantity: quantity,
              });
              const atCapacity = Boolean(stepDisabled);
              // Место разобрано целиком, и эта позиция в выбор не входит: её
              // нельзя ни выбрать, ни увеличить. Красный текст ей не нужен —
              // причина названа одним сообщением над списком.
              const blockedByFullCapacity = isCapacityFull && !isSelected;
              // Красная строка — ответ на конкретную попытку набрать больше, чем
              // помещается, а не постоянная метка занятого места.
              const showFitWarn = doesNotFit && attemptedKeys.includes(key) && !blockedByFullCapacity;

              return (
                <div className={`gaz-upsell__row${isSelected ? ' is-picked' : ''}`} key={key}>
                  <div className="gaz-upsell__name">
                    <span className="gaz-upsell__product">
                      {formatNomenclatureName(item.product_calculator_name || 'Не указано')}
                    </span>
                    <span className="gaz-upsell__meta">
                      {item.manufacturer || 'Производство не указано'} · {item.category || 'Без категории'}
                    </span>
                    {/* До подтверждения товар только выбран в окне: «уже в заказе»
                        он станет после добавления и закрытия. */}
                    {isSelected && (
                      <span className="gaz-upsell__meta gaz-upsell__meta--picked">Выбрано для добавления</span>
                    )}
                  </div>

                  <div className="gaz-upsell__cell">
                    <span className="gaz-upsell__cell-value">
                      {capacityText || 'Норма поддона не заведена'}
                    </span>
                    {/* Подсказка о пределе: максимум не выставляется автоматически.
                        Учитывается собственное количество позиции — место, которое
                        она уже заняла в выборе, для неё остаётся доступным. */}
                    {maxHint ? (
                      // «Поместится до 0 шт.» не пишем: ноль — это не предел, а
                      // отсутствие места. Выбранной позиции предел считается
                      // с учётом её собственного места и остаётся обычным.
                      maxHint.maxPallets <= 0 && !isSelected ? (
                        <small className="gaz-upsell__hint">Нет свободного места</small>
                      ) : (
                        <small className="gaz-upsell__hint">
                          {maxHint.maxUnits !== null
                            ? `Поместится до ${fmt(maxHint.maxUnits)} ${formatUnit(item.unit) || 'шт'}. · ${fmt(maxHint.maxPallets)} ${palletsWord(maxHint.maxPallets)}`
                            : `Поместится до ${fmt(maxHint.maxPallets)} ${palletsWord(maxHint.maxPallets)}`}
                        </small>
                      )
                    ) : (
                      <small className="gaz-upsell__hint is-warn">
                        Нельзя определить вместимость: нет нормы поддона
                      </small>
                    )}
                  </div>

                  <div className="gaz-upsell__cell gaz-upsell__cell--qty">
                    <span className="gaz-upsell__cell-label">Кол-во</span>
                    {perPallet && isPalletOnlyItem(item) ? (
                      <>
                        <div className="gaz-upsell__stepper">
                          <button
                            type="button"
                            onClick={() => stepByPallet(item, key, -1)}
                            disabled={currentPallets <= 1}
                            title="Убрать поддон"
                          >
                            −
                          </button>
                          <span>{quantity}</span>
                          <button
                            type="button"
                            onClick={() => stepByPallet(item, key, 1)}
                            disabled={Boolean(stepDisabled) || blockedByFullCapacity}
                            title={stepDisabled || blockedByFullCapacity
                              ? 'Больше не помещается в машину'
                              : 'Добавить поддон'}
                          >
                            +
                          </button>
                        </div>
                        <small className="gaz-upsell__hint">
                          {currentPallets} {palletsWord(currentPallets)} × {fmt(perPallet)} шт.
                        </small>
                        {/* Подпись объясняет, почему «+» недоступен именно этой
                            строке. Когда место разобрано целиком, причина уже
                            названа одним сообщением над списком — повторять её
                            на каждой строке незачем. */}
                        {atCapacity && !blockedByFullCapacity && (
                          <small className="gaz-upsell__hint is-warn">
                            Достигнута свободная вместимость машины
                          </small>
                        )}
                      </>
                    ) : (
                      <>
                        <input
                          type="number"
                          min="0"
                          step="any"
                          value={quantity !== undefined ? quantity : '1'}
                          onChange={(event) => setQuantity(key, event.target.value)}
                          onBlur={() => roundQuantityToPallet(item, key)}
                          disabled={blockedByFullCapacity}
                        />
                        <small className="gaz-upsell__hint">
                          {perPallet ? `${fmt(perPallet)} шт на поддоне` : (item.unit || 'шт')}
                        </small>
                      </>
                    )}
                  </div>

                  <div className="gaz-upsell__cell gaz-upsell__cell--price">
                    <span className="gaz-upsell__cell-label">Цена</span>
                    <span className={`gaz-upsell__cell-value${priceIsUsable ? '' : ' is-warn'}`}>
                      {priceIsUsable ? `${parsePriceValue(price).toLocaleString('ru-RU')} ₽` : GAZ_PRICE_ON_REQUEST}
                    </span>
                    <small className="gaz-upsell__hint">
                      {priceIsUsable
                        ? `${paymentMethod === 'cash' ? 'нал' : 'безнал'} · за ${formatUnit(item.unit) || 'ед.'}`
                        : 'добавить нельзя'}
                    </small>
                  </div>

                  <div className="gaz-upsell__cell gaz-upsell__cell--stock">
                    {/* Складских остатков и сроков в каталоге нет — есть только
                        подтверждённое правило завода для U-блоков (gazUpsellMetrics).
                        Ничего сверх него не выдумывается. */}
                    {stock && (
                      <>
                        <span className="gaz-upsell__stock-status">{stock.status}</span>
                        <span className="gaz-upsell__stock-terms">{stock.terms}</span>
                      </>
                    )}
                    {item.price_update_date && (
                      <small className="gaz-upsell__hint">Цена от {item.price_update_date}</small>
                    )}
                  </div>

                  <div className="gaz-upsell__pick">
                    <button
                      type="button"
                      className={`gaz-upsell__pick-btn${isSelected ? ' is-picked' : ''}`}
                      onClick={() => toggleSelected(key)}
                      disabled={!priceIsUsable || (!isSelected && doesNotFit) || blockedByFullCapacity}
                      title={!priceIsUsable
                        ? 'У позиции нет цены — добавить нельзя'
                        : (blockedByFullCapacity
                          ? 'Свободное место распределено полностью'
                          : (!isSelected && doesNotFit ? 'Не помещается в свободное место машины' : undefined))}
                    >
                      {isSelected ? 'Убрать' : 'Выбрать'}
                    </button>
                    {showFitWarn && (
                      <small className="gaz-upsell__fit-warn">
                        Не помещается: требуется ещё {fmt(pallets - Math.max(allowance, 0))}{' '}
                        {palletPlacesWord(pallets - Math.max(allowance, 0))},
                        свободно {fmt(Math.max(allowance, 0))}
                      </small>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Товары без цены не занимают начало списка: они уходят отдельной
                группой вниз. Если поиск нашёл только их, группа раскрыта сразу —
                иначе список выглядел бы пустым. */}
            {pricelessRows.length > 0 && (
              <div className="gaz-upsell__priceless">
                <button
                  type="button"
                  className="gaz-upsell__priceless-toggle"
                  onClick={() => setShowPriceless((prev) => !prev)}
                  aria-expanded={pricelessOpen}
                >
                  Без цены — {pricelessRows.length} {itemsWord(pricelessRows.length)}
                </button>

                {pricelessOpen && pricelessRows.map(({ item, key }) => (
                  <div className="gaz-upsell__priceless-row" key={key}>
                    <span className="gaz-upsell__product">
                      {formatNomenclatureName(item.product_calculator_name || 'Не указано')}
                    </span>
                    <span className="gaz-upsell__meta">
                      {item.manufacturer || 'Производство не указано'} · {item.category || 'Без категории'}
                    </span>
                    <span className="gaz-upsell__priceless-note">{GAZ_PRICE_ON_REQUEST} · добавить нельзя</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="gaz-upsell__foot">
          <button type="button" className="gaz-upsell__btn" onClick={onClose}>
            Отмена
          </button>
          <button
            type="button"
            className="gaz-upsell__btn gaz-upsell__btn--primary"
            onClick={handleSubmit}
            disabled={selectedRows.length === 0 || isOverCapacity || hasInvalidQuantity || hasPricelessSelection}
          >
            {addButtonLabel({ count: selectedRows.length, total: selectedTotal })}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default GazUpsellModal;
